import type { C1_Room } from "../model/C1_Room";
import type { Drag, Rect, ResizePreview } from "../types/C1_AppTypes";

export type RoomPreviewPoint = { x: number; y: number };

/** Translates a room without changing its shape or adopting a reference floor's dimensions. */
export function createRoomMovePreview(room: C1_Room, dx: number, dy: number): ResizePreview {
  const rect = room.rect();
  const preview: ResizePreview = {
    room,
    rect: { x1: rect.x1 + dx, x2: rect.x2 + dx, y1: rect.y1 + dy, y2: rect.y2 + dy },
  };
  preview.polygonPoints = translateRoomPoints(room.polygonPoints(), room, preview);
  return preview;
}

/** Builds the same wall topology used at commit, for both inward and outward segment moves. */
export function createWallResizePreview(
  room: C1_Room,
  drag: Extract<Drag, { type: "resize" }>,
  coordinate: number,
): ResizePreview | undefined {
  const wall = room.walls?.findFirst((candidate) => candidate.id === drag.wallId);
  if (!wall) return undefined;
  const preview: ResizePreview = {
    room,
    rect: room.rect(),
    extrudesWall: room.wallHasCollinearNeighbor(wall),
    wallCoordinate: coordinate,
  };
  preview.polygonPoints = roomPreviewPoints(room, preview, drag);
  preview.rect = polygonBounds(preview.polygonPoints, preview.rect);
  return preview;
}

/** Derives the preview bounds from all vertices, including inset walls and connector segments. */
function polygonBounds(points: readonly RoomPreviewPoint[], fallback: Rect): Rect {
  if (points.length === 0) return fallback;
  const rect = { x1: Infinity, y1: Infinity, x2: -Infinity, y2: -Infinity };
  for (const point of points) {
    rect.x1 = Math.min(rect.x1, point.x);
    rect.y1 = Math.min(rect.y1, point.y);
    rect.x2 = Math.max(rect.x2, point.x);
    rect.y2 = Math.max(rect.y2, point.y);
  }
  return rect;
}

/** Builds the transient polygon shown while a room is moved or one of its walls is resized. */
export function roomPreviewPoints(
  room: C1_Room,
  preview: ResizePreview | null | undefined,
  drag: Drag | null,
): RoomPreviewPoint[] {
  if (preview?.room === room && preview.polygonPoints) return clonePoints(preview.polygonPoints);
  const modelPoints = room.polygonPoints();
  if (preview?.room !== room || !drag || (drag.type !== "room" && drag.type !== "resize")) return modelPoints;
  if (drag.roomId !== room.id) return modelPoints;

  if (drag.type === "room") return translateRoomPoints(modelPoints, room, preview);

  const wallIndex = findWallIndex(room, drag.wallId);
  if (wallIndex < 0) return modelPoints;
  const targetCoordinate = preview.wallCoordinate ?? coordinateFromRect(drag.side, preview);
  if (preview.extrudesWall) {
    return extrudedRoomPoints(modelPoints, wallIndex, drag.side, drag.segmentStart, drag.segmentEnd, targetCoordinate);
  }
  return resizedRoomPoints(modelPoints, wallIndex, drag.side, targetCoordinate);
}

/** Translates every polygon vertex by the preview rectangle offset. */
function translateRoomPoints(points: RoomPreviewPoint[], room: C1_Room, preview: ResizePreview): RoomPreviewPoint[] {
  const modelRect = room.rect();
  const dx = preview.rect.x1 - modelRect.x1;
  const dy = preview.rect.y1 - modelRect.y1;
  const translated: RoomPreviewPoint[] = [];
  for (const point of points) translated.push({ x: point.x + dx, y: point.y + dy });
  return translated;
}

/** Moves both endpoints of the selected wall without changing unrelated collinear vertices. */
function resizedRoomPoints(
  points: RoomPreviewPoint[],
  wallIndex: number,
  side: "left" | "right" | "top" | "bottom",
  target: number,
): RoomPreviewPoint[] {
  const result = clonePoints(points);
  const coordinate = side === "left" || side === "right" ? "x" : "y";
  result[wallIndex][coordinate] = target;
  result[wallIndex + 1][coordinate] = target;
  if (wallIndex === 0) result[result.length - 1][coordinate] = target;
  if (wallIndex === result.length - 2) result[0][coordinate] = target;
  return result;
}

/** Replaces the selected edge with the same orthogonal detour used when the drag is committed. */
function extrudedRoomPoints(
  points: RoomPreviewPoint[],
  wallIndex: number,
  side: "left" | "right" | "top" | "bottom",
  segmentStart: number,
  segmentEnd: number,
  target: number,
): RoomPreviewPoint[] {
  const vertical = side === "left" || side === "right";
  const start = Math.min(segmentStart, segmentEnd);
  const end = Math.max(segmentStart, segmentEnd);
  if (end - start < 0.000001) return clonePoints(points);

  const wallCount = points.length - 1;
  const sourceStart = points[wallIndex];
  const sourceEnd = points[wallIndex + 1];
  const previousStart = wallIndex === 0 ? points[wallCount - 1] : points[wallIndex - 1];
  const nextEnd = wallIndex === wallCount - 1 ? points[1] : points[wallIndex + 2];
  const previousIsCollinear = segmentsAreCollinear(previousStart, sourceStart, sourceEnd);
  const nextIsCollinear = segmentsAreCollinear(sourceStart, sourceEnd, nextEnd);
  const targetStart = vertical ? { x: target, y: sourceStart.y } : { x: sourceStart.x, y: target };
  const targetEnd = vertical ? { x: target, y: sourceEnd.y } : { x: sourceEnd.x, y: target };

  const endpoints = clonePoints(points.slice(1));
  let startPoint = { ...points[0] };
  if (!previousIsCollinear) {
    if (wallIndex === 0) {
      startPoint = targetStart;
      endpoints[wallCount - 1] = targetStart;
    } else {
      endpoints[wallIndex - 1] = targetStart;
    }
  }
  if (!nextIsCollinear && wallIndex === wallCount - 1) startPoint = targetEnd;

  const replacement: RoomPreviewPoint[] = [];
  if (previousIsCollinear) replacement.push(targetStart);
  replacement.push(targetEnd);
  if (nextIsCollinear) replacement.push({ ...sourceEnd });

  const result: RoomPreviewPoint[] = [{ ...startPoint }];
  for (let index = 0; index < wallIndex; index += 1) appendDistinctPoint(result, endpoints[index]);
  for (const point of replacement) appendDistinctPoint(result, point);
  for (let index = wallIndex + 1; index < endpoints.length; index += 1) appendDistinctPoint(result, endpoints[index]);
  return result;
}

/** Tests whether two segments sharing the middle point continue on one line. */
function segmentsAreCollinear(first: RoomPreviewPoint, middle: RoomPreviewPoint, last: RoomPreviewPoint): boolean {
  const firstVector = { x: middle.x - first.x, y: middle.y - first.y };
  const secondVector = { x: last.x - middle.x, y: last.y - middle.y };
  const firstLength = Math.hypot(firstVector.x, firstVector.y);
  const secondLength = Math.hypot(secondVector.x, secondVector.y);
  if (firstLength < 0.000001 || secondLength < 0.000001) return false;
  const cross = Math.abs(firstVector.x * secondVector.y - firstVector.y * secondVector.x);
  return cross <= 0.000001 * firstLength * secondLength;
}

/** Adds a point unless it duplicates the preceding vertex. */
function appendDistinctPoint(points: RoomPreviewPoint[], point: RoomPreviewPoint): void {
  const previous = points.at(-1);
  if (previous && Math.hypot(point.x - previous.x, point.y - previous.y) < 0.000001) return;
  points.push({ ...point });
}

function clonePoints(points: RoomPreviewPoint[]): RoomPreviewPoint[] {
  const result: RoomPreviewPoint[] = [];
  for (const point of points) result.push({ ...point });
  return result;
}

function findWallIndex(room: C1_Room, wallId: string): number {
  if (!room.walls) return -1;
  for (let index = 0; index < room.walls.size; index += 1) {
    if (room.walls.at(index)?.id === wallId) return index;
  }
  return -1;
}

function coordinateFromRect(side: "left" | "right" | "top" | "bottom", preview: ResizePreview): number {
  if (side === "left") return preview.rect.x1;
  if (side === "right") return preview.rect.x2;
  if (side === "top") return preview.rect.y1;
  return preview.rect.y2;
}
